import { FiMinusCircle } from 'react-icons/fi';

export const ellipsisSubstitutionData = {
  courseId: 'ellipsis-substitution',
  courseType: 'grammar',
  title: 'Ellipsis & Substitution',
  icon: FiMinusCircle,
  overview: {
    impact: 'Avoid repetition and achieve natural, cohesive writing',
    focus: 'Omitting or replacing words to avoid redundancy',
    uses: 'Writing Tasks 1 & 2, Speaking fluency',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Ellipsis (Omission)',
      content: `<p>Leaving out words that are understood from context:</p>`,
      examples: [
        {
          label: 'Full',
          text: 'Some people prefer city life and some people prefer rural life.'
        },
        {
          label: 'Ellipsis',
          text: 'Some people prefer city life and some rural life.'
        }
      ],
      points: [
        'Verb ellipsis: \'She can speak French and he can too.\' → \'She can speak French and he can too.\'',
        'Noun ellipsis: \'I\'ll have the blue shirt and she\'ll have the red.\' (shirt omitted)'
      ]
    },
    {
      title: 'Substitution',
      content: `<p>Replacing words to avoid repetition:</p>`,
      points: [
        '\'one/ones\' for nouns: \'I don\'t like this shirt. I prefer that one.\'',
        '\'so\' for clauses: \'Will it rain? I think so.\'',
        '\'do so/do\' for verbs: \'She asked me to help and I did.\'',
        '\'not\' for negative: \'Is he coming? I hope not.\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Identify the Technique',
      type: 'select',
      instructions: 'Is this ellipsis or substitution?',
      questions: [
        {
          text: 'I wanted the blue car but bought the red. (car omitted)',
          options: [
            'Ellipsis',
            'Substitution'
          ],
          answer: 'Ellipsis',
          explanation: 'The word \'car\' is simply omitted — understood from context.'
        },
        {
          text: 'This laptop is old. I need a new one.',
          options: [
            'Substitution',
            'Ellipsis'
          ],
          answer: 'Substitution',
          explanation: '\'One\' substitutes for \'laptop\'.'
        }
      ]
    },
    {
      title: 'Identify the Ellipsis',
      type: 'select',
      instructions: 'What word(s) have been omitted (ellipsis) in each sentence?',
      questions: [
        {
          text: 'She can play guitar and he can too. — What is omitted after \'can\'?',
          options: [
            'play guitar',
            'she',
            'too'
          ],
          answer: 'play guitar',
          explanation: 'The full form is \'he can play guitar too\' — \'play guitar\' is omitted to avoid repetition.'
        },
        {
          text: 'I ordered a black coffee and my friend a latte. — What is missing?',
          options: [
            'ordered',
            'a',
            'black'
          ],
          answer: 'ordered',
          explanation: 'The full form is \'my friend ordered a latte\' — the verb \'ordered\' is omitted.'
        },
        {
          text: 'Some students passed the test and some didn\'t. — What is omitted after \'didn\'t\'?',
          options: [
            'pass the test',
            'some students',
            'the test'
          ],
          answer: 'pass the test',
          explanation: 'The full form is \'some didn\'t pass the test\' — \'pass the test\' is omitted.'
        },
        {
          text: 'He wanted to leave early but couldn\'t. — What is omitted?',
          options: [
            'leave early',
            'he wanted',
            'to'
          ],
          answer: 'leave early',
          explanation: 'The full form is \'couldn\'t leave early\' — \'leave early\' is omitted after the modal.'
        }
      ]
    },
    {
      title: 'Substitution with So/Not',
      type: 'fill-in',
      instructions: 'Complete each response with \'so\' or \'not\'.',
      questions: [
        {
          text: 'Will it be sunny tomorrow? — I hope ___.',
          answer: 'so',
          explanation: '\'I hope so\' = I hope it will be sunny. \'So\' substitutes for the positive clause.'
        },
        {
          text: 'Is the exam cancelled? — I\'m afraid ___.',
          answer: 'not',
          explanation: '\'I\'m afraid not\' = I\'m afraid it isn\'t cancelled. \'Not\' substitutes for the negative.'
        },
        {
          text: 'Do you think they\'ll win the match? — I think ___.',
          answer: 'so',
          explanation: '\'I think so\' = I think they will win. \'So\' substitutes for the positive clause.'
        },
        {
          text: 'Has the train already left? — I believe ___.',
          answer: 'so',
          explanation: '\'I believe so\' = I believe it has left. \'So\' replaces the entire clause.'
        }
      ]
    },
    {
      title: 'Rewrite with Ellipsis',
      type: 'writing',
      instructions: 'Shorten each sentence by removing repeated words (using ellipsis or substitution).',
      questions: [
        {
          text: 'Tom likes football and Jerry likes football too.',
          sampleAnswer: 'Tom likes football and Jerry does too.',
          explanation: 'Replace the repeated verb phrase with \'does too\' or use ellipsis: \'Tom likes football and so does Jerry.\''
        },
        {
          text: 'I wanted the blue shirt, but I bought the red shirt.',
          sampleAnswer: 'I wanted the blue shirt, but I bought the red.',
          explanation: 'Omit the repeated noun \'shirt\' — it is understood from context.'
        },
        {
          text: 'She can sing well and he can sing well too.',
          sampleAnswer: 'She can sing well and he can too.',
          explanation: 'Omit \'sing well\' after the modal \'can\' — it is understood from the first clause.'
        }
      ]
    }
  ],
  tips: [
    'Ellipsis = omit repeated words',
    'Substitution = replace with pronouns/pro-forms (one, so, do)',
    'Improves coherence and reduces word count',
    'Shows natural, sophisticated English'
  ],
  dailyPractice: [
    'Practice ellipsis & substitution concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Ellipsis & Substitution: Techniques to avoid repetition. Ellipsis omits words; substitution replaces them with pro-forms. Key for cohesion in IELTS Writing.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...ellipsisSubstitutionData,
      title: ellipsisSubstitutionData.title + ' (Foundations)',
      overview: { ...ellipsisSubstitutionData.overview, duration: '15-20 minutes' },
      sections: ellipsisSubstitutionData.sections.slice(0, Math.max(2, Math.ceil(ellipsisSubstitutionData.sections.length / 2))),
      exercises: ellipsisSubstitutionData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...ellipsisSubstitutionData,
      title: ellipsisSubstitutionData.title + ' (Advanced)',
      overview: { ...ellipsisSubstitutionData.overview, duration: '30-35 minutes' },
      sections: ellipsisSubstitutionData.sections,
      exercises: ellipsisSubstitutionData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(ellipsisSubstitutionData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return ellipsisSubstitutionData;
}
